import { useMemo } from 'react'

import { Text } from '@wartek-id/text-v1'
import { Shimmer } from '@wartek-id/shimmer'
import { Link } from '@wartek-id/link'
import { Button } from '@wartek-id/button-v1'

import {
  useAPIContentPerilakuKerja,
  useAPIKinerja,
} from 'api/kinerja/useAPIKinerja'
import { Header } from 'app/KinerjaShared/components/Header'
import usePeriodContext from 'app/KinerjaShared/hooks/usePeriodContext'
import { useSafeBackNavigation } from 'app/KinerjaShared/hooks/useSafeBackNavigation'
import {
  SectionCard,
  SectionCardBody,
  SectionCardFooter,
} from 'app/KinerjaShared/components/SectionCard'
import { PENGELOLAAN_KINERJA_PAMONG_PEGAWAI_PATH } from 'app/KinerjaShared/constants/path'

import type { MultiPeriodPayload } from 'types/kinerja'

type PerilakuKerjaProps = {
  periodParams?: MultiPeriodPayload
}

export function PerilakuKerja(props: PerilakuKerjaProps) {
  const { periodNo, periodYear } = usePeriodContext()

  // -----------------------------------------
  // Resolve period params
  // -----------------------------------------
  const resolvedPeriodParams = useMemo(() => {
    if (props.periodParams) return props.periodParams

    // Default with current period
    return {
      skpPeriod: periodNo,
      skpYear: periodYear,
    }
  }, [props.periodParams, periodNo, periodYear])

  // -----------------------------------------
  // Handle safe back navigation
  // -----------------------------------------
  const { handleBack } = useSafeBackNavigation({
    fallbackRoute: PENGELOLAAN_KINERJA_PAMONG_PEGAWAI_PATH,
  })

  // -----------------------------------------
  // Get Perilaku Kerja content
  // -----------------------------------------
  const { data: perencanaanData, isLoading: isPerencanaanLoading } =
    useAPIKinerja(
      {
        staleTime: 60 * 60 * 1000,
      },
      {
        tipe: 'SPNF',
        skp_year: resolvedPeriodParams.skpYear,
        skp_period: resolvedPeriodParams.skpPeriod,
      }
    )

  const currentPerilakuKerja = perencanaanData?.data?.prilakuKerja

  // -----------------------------------------
  // Get Perilaku Kerja content
  // -----------------------------------------
  const {
    data: contentPerilakuKerjaData,
    isLoading: isContentPerilakuKerjaLoading,
  } = useAPIContentPerilakuKerja(
    {
      tipe: 'SPNF',
      periodParams: {
        skp_year: resolvedPeriodParams.skpYear,
        skp_period: resolvedPeriodParams.skpPeriod,
      },
    },
    {
      staleTime: 6 * 60 * 60 * 1000, // 6 hour
      enabled: true,
    }
  )

  // -----------------------------------------
  // Resolve Perilaku Kerja data
  // -----------------------------------------
  const perilakuKerjaItems = useMemo(() => {
    const perilakuKerja = currentPerilakuKerja || []
    const aspekList = contentPerilakuKerjaData?.data?.aspek || []

    return perilakuKerja.map((pk) => {
      const aspek = aspekList.find((a) => a.id === pk.aspekId)
      const indikator = aspek?.indikator.find((i) => i.id === pk.indikatorId)
      const ekspektasi = indikator?.ekspektasi.find(
        (e) => e.id === pk.ekspektasiId
      )

      return {
        aspek: aspek?.value || '',
        indikator: indikator?.value || '',
        ekspektasi: ekspektasi?.value || '',
      }
    })
  }, [currentPerilakuKerja, contentPerilakuKerjaData])

  // -----------------------------------------
  // Other derived states
  // -----------------------------------------
  const isLoading = isPerencanaanLoading || isContentPerilakuKerjaLoading

  return (
    <main className="pt-12 px-8 pb-8 mx-auto max-w-7xl ">
      <div className="flex justify-between items-center">
        <Header title="Perilaku Kerja" className="mb-9" onBack={handleBack} />
      </div>
      <SectionCard>
        <SectionCardBody className="p-9">
          <div className="space-y-1.5 mb-9">
            <Text variant="heading-sm" as="h2">
              Daftar Perilaku Kerja pilihan Anda
            </Text>
            <Text className="mt-1.5">
              Anda tidak perlu kumpulkan Bukti Dukung. Cukup laksanakan dalam
              keseharian Anda di luar sistem.
            </Text>
            <Link
              href={`${PENGELOLAAN_KINERJA_PAMONG_PEGAWAI_PATH}/panduan/perilaku-kerja`}
              target="_blank"
              rel="noopener"
              className="!pl-0 font-bold"
            >
              Cek Daftar Perilaku Kerja
            </Link>
          </div>
          <div className="space-y-9">
            {isLoading ? (
              <>
                <Shimmer height="220px" width="100%" />
                <Shimmer height="220px" width="100%" />
                <Shimmer height="220px" width="100%" />
                <Shimmer height="220px" width="100%" />
                <Shimmer height="220px" width="100%" />
              </>
            ) : (
              perilakuKerjaItems.map((perilakuKerja, index) => {
                return (
                  <section
                    key={index}
                    className="border border-subdued rounded p-6 space-y-6"
                  >
                    <Text variant="heading-lg" as="h3">
                      {perilakuKerja.aspek}
                    </Text>
                    <dl className="space-y-6">
                      <div className="space-y-1">
                        <Text as="dt" variant="body" color="subdued">
                          Indikator
                        </Text>
                        <Text as="dd" variant="action">
                          {perilakuKerja.indikator}
                        </Text>
                      </div>
                      <div className="space-y-1">
                        <Text as="dt" variant="body" color="subdued">
                          Ekspektasi Khusus Pimpinan
                        </Text>
                        <Text as="dd" variant="action">
                          {perilakuKerja.ekspektasi}
                        </Text>
                      </div>
                    </dl>
                  </section>
                )
              })
            )}
          </div>
        </SectionCardBody>
        <SectionCardFooter className="justify-end">
          <Button size="lg" onClick={handleBack}>
            Saya mengerti
          </Button>
        </SectionCardFooter>
      </SectionCard>
    </main>
  )
}
